import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import Icon, { type IconName } from '../../components/Icon';
import { initialsOf } from '../team/roleMeta';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/distributor/dashboard', label: 'Dashboard', icon: 'home', end: true },
  { to: '/distributor/orders', label: 'Secondary orders', icon: 'list' },
  { to: '/distributor/primary-orders', label: 'Primary orders', icon: 'list' },
  { to: '/distributor/retailers', label: 'Retailers', icon: 'users' },
];

/**
 * The distributor's own shell — simpler than the main AppShell.
 *
 * Distributors see only their portal: the orders flowing through their
 * territory, their own purchase orders back to the company, and the
 * retailers assigned to them. There is no visit tracking, no attendance,
 * no expenses — just the commercial relationship.
 */
export default function DistributorPortalLayout() {
  const { user, company, signOut } = useAuth();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          {company?.logoUrl
            ? <img className="brand-logo" src={company.logoUrl} alt={company.name} />
            : <div className="brand-mark">S</div>}
          <div style={{ minWidth: 0 }}>
            <div className="brand-name">Salestrendz</div>
            {company ? (
              <div className="small faint" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {company.name}
              </div>
            ) : null}
          </div>
        </div>

        <nav className="nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
            >
              <Icon name={item.icon} size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="who">
            <div className="avatar">{initialsOf(user?.fullName ?? '?')}</div>
            <div className="who-text grow">
              <div className="who-name">{user?.fullName}</div>
              <div className="who-role">Distributor</div>
            </div>
            <button
              type="button"
              className="icon-btn"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => void signOut()}
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
